import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CreditCard, Clock, PackagePlus, Lock } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayHoursPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    vat_or_cf: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/pay/hours/${token}`);
        setIntent(data);
        setForm((f) => ({
          ...f,
          name: data.prefill?.name || "",
          email: data.prefill?.email || "",
          phone: data.prefill?.phone || "",
        }));
      } catch (e) {
        setError(e?.response?.data?.detail || "Link non valido o scaduto");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const pay = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/pay/hours/${token}/checkout`, {
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il pagamento");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="rounded-sm border-zinc-200 bg-white p-8 max-w-md w-full text-center">
          <Lock className="h-8 w-8 text-zinc-400 mx-auto mb-3" strokeWidth={1.5} />
          <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950 mb-2">Link non disponibile</h2>
          <p className="text-sm text-zinc-500">{error}</p>
          <p className="text-xs text-zinc-400 mt-4">Contatta l&apos;assistenza per un nuovo link.</p>
        </Card>
      </div>
    );
  }

  const isCarnet = intent.package === "carnet_10h";

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header brand */}
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo size="xl" />
          <div>
            <div className="font-display text-xl font-bold tracking-tighter text-zinc-950">Wineemotion</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Assistenza · Acquisto ore</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Riepilogo pacchetto */}
          <Card className="rounded-sm border-brand bg-brand-muted/30 p-6 md:col-span-2 h-fit">
            <div className="flex items-center gap-2 mb-3">
              {isCarnet ? <PackagePlus className="h-4 w-4 text-brand" /> : <Clock className="h-4 w-4 text-brand" />}
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-medium">
                {isCarnet ? "Carnet 10 ore" : "Ora singola"}
              </div>
            </div>
            <div className="font-display text-4xl font-bold tracking-tighter text-zinc-950">
              {intent.hours}<span className="text-lg font-normal ml-1 text-zinc-500">ore</span>
            </div>
            <div className="mt-2 text-2xl font-semibold text-brand">€{intent.amount.toFixed(2)}</div>
            {isCarnet && (
              <div className="text-xs text-zinc-500 mt-1">€{(intent.amount / 10).toFixed(2)}/h</div>
            )}
            <div className="border-t border-brand/20 mt-4 pt-4 text-xs text-zinc-600 leading-relaxed">
              Il pacchetto viene aggiunto al tuo saldo ore appena il pagamento è confermato.
              Le ore sono utilizzabili per interventi di assistenza tecnica sui tuoi dispenser Wineemotion.
            </div>
          </Card>

          {/* Form dati fiscali */}
          <Card className="rounded-sm border-zinc-200 bg-white p-6 md:col-span-3">
            <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-950 mb-1">Dati fatturazione</h2>
            <p className="text-xs text-zinc-500 mb-5">Necessari per l&apos;emissione della ricevuta di pagamento.</p>
            <form onSubmit={pay} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Nome / Ragione sociale *</Label>
                <Input
                  data-testid="pay-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1 rounded-sm"
                  placeholder="Es. Mario Rossi / Bar Roma SRL"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Email *</Label>
                  <Input
                    data-testid="pay-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    placeholder="mario@example.com"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Telefono</Label>
                  <Input
                    data-testid="pay-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 rounded-sm"
                    placeholder="+39 ..."
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Indirizzo (via, città, CAP)</Label>
                <Textarea
                  data-testid="pay-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">P.IVA / Codice Fiscale</Label>
                <Input
                  data-testid="pay-vat"
                  value={form.vat_or_cf}
                  onChange={(e) => setForm({ ...form, vat_or_cf: e.target.value })}
                  className="mt-1 rounded-sm"
                />
              </div>

              <Button
                data-testid="pay-submit"
                type="submit"
                disabled={busy}
                className="w-full mt-4 bg-brand hover:bg-brand-dark text-white rounded-sm h-11"
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirect a Stripe...</> : <><CreditCard className="h-4 w-4 mr-2" /> Paga €{intent.amount.toFixed(2)} con Stripe</>}
              </Button>
              <p className="text-[11px] text-zinc-500 text-center mt-2">
                Pagamento sicuro tramite Stripe. Accettiamo carte Visa, Mastercard, American Express.
              </p>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
